import { useAuthInfo } from "@propelauth/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isToday } from "date-fns";
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, Loader2 } from "lucide-react";
import MeetingDocTabs, { type DocTab } from "../../../components/docs/MeetingDocTabs";
import { authorizedApiClient } from "../../../services/config";
import {
  triggerMeetingPrep,
  triggerMeetingFollowup,
} from "../../../services/meetingPrepEndpoint";
import {
  getWorkspaceMeetingDocs,
  type MeetingDoc,
} from "../../../services/meetingDocsEndpoint";
import {
  getMeetingDocMarkdown,
  normalizeMeetingDocStatus,
} from "../../../services/meetingDocsHelpers";
import type {
  WorkspaceObjectSearchRequest,
  WorkspaceObjectSearchResponse,
} from "../../../services/workspace/workspaceEndpoints.interface";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import notify from "../../../utils/notify";
import ObjectDetailSheet from "../account-details/account-custom-object/renderers/ObjectDetailSheet";
import MeetingsBoard from "./components/MeetingsBoard";
import MeetingsHeader from "./components/MeetingsHeader";
import MeetingsListTable from "./components/MeetingsListTable";
import type {
  MeetingActionFilter,
  MeetingBoardGroupBy,
  MeetingDateFilter,
  MeetingDisplayMode,
  MeetingPrepStatus,
  MeetingRowData,
  MeetingViewFilter,
} from "./meetingsTypes";
import { MEETING_GROUP_ORDER } from "./meetingsTypes";
import { parseMeetingRecords } from "./meetingsUtils";

// Selected from live meetings payload keys in DB for this workspace.
// These map directly to title/date/attendee parsing in meetingsUtils.
const MEETING_SELECT_FIELDS = [
  "native_title",
  "native_start_time_tz",
  "native_participants_emails",
] as const;

const withObjectIncludes = (payload: WorkspaceObjectSearchRequest): WorkspaceObjectSearchRequest => ({
  ...payload,
  include: [
    {
      object_type: "accounts",
      select: ["name"],
      limit: 1,
    },
    {
      object_type: "users",
      select: ["name", "first_name", "last_name", "email"],
      limit: 10,
    },
  ],
});

const sortByGroupAndTime = (meetings: MeetingRowData[]) => {
  const groupOrder: Record<string, number> = {};
  MEETING_GROUP_ORDER.forEach((value, index) => {
    groupOrder[value] = index;
  });

  return [...meetings].sort((left, right) => {
    const leftGroup = groupOrder[left.group] ?? Number.MAX_SAFE_INTEGER;
    const rightGroup = groupOrder[right.group] ?? Number.MAX_SAFE_INTEGER;

    if (leftGroup !== rightGroup) return leftGroup - rightGroup;

    if (!left.when && !right.when) return left.title.localeCompare(right.title);
    if (!left.when) return 1;
    if (!right.when) return -1;

    if (left.group === "Coming up") {
      return left.when.getTime() - right.when.getTime();
    }

    return right.when.getTime() - left.when.getTime();
  });
};

const MeetingsPage = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const authInfo = useAuthInfo();
  const queryClient = useQueryClient();

  const [activeView, setActiveView] = useState<MeetingViewFilter>("all");
  const [displayMode, setDisplayMode] = useState<MeetingDisplayMode>("list");
  const [groupBy, setGroupBy] = useState<MeetingBoardGroupBy>("time_group");
  const [showFilters, setShowFilters] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [dateFilter, setDateFilter] = useState<MeetingDateFilter>("all");
  const [actionFilter, setActionFilter] = useState<MeetingActionFilter>("all");
  const [preparingMeetingIds, setPreparingMeetingIds] = useState<Set<string>>(new Set());
  const [followingUpMeetingIds, setFollowingUpMeetingIds] = useState<Set<string>>(new Set());
  const [selectedMeeting, setSelectedMeeting] = useState<MeetingRowData | null>(null);
  const [initialDocTab, setInitialDocTab] = useState<DocTab | undefined>();
  const apiWorkspaceId = workspace?.id;

  const meetingsQuery = useQuery<MeetingRowData[]>({
    queryKey: ["meetings-page", workspace?.id],
    enabled: !!workspace?.id,
    queryFn: async () => {
      const workspaceId = workspace?.id;
      if (!workspaceId) return [];

      const payload: WorkspaceObjectSearchRequest = {
        object_type: "meetings",
        select: [...MEETING_SELECT_FIELDS],
        sort: [{ field: "created_at", order: "desc" }],
        limit: 200,
      };

      const endpoint = `/workspace/${workspaceId}/object-search`;

      try {
        const response = await authorizedApiClient.post<WorkspaceObjectSearchResponse>(
          endpoint,
          withObjectIncludes(payload),
        );
        return parseMeetingRecords(response.data?.data || []);
      } catch (error: unknown) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;

        // Some workspaces don't have account/user relationships configured for meetings.
        if (status === 400 || status === 422) {
          const fallback = await authorizedApiClient.post<WorkspaceObjectSearchResponse>(
            endpoint,
            payload,
          );
          return parseMeetingRecords(fallback.data?.data || []);
        }

        throw error;
      }
    },
  });

  const meetingPrepDocsQuery = useQuery<MeetingDoc[]>({
    queryKey: ["meetings-page-prep-docs", apiWorkspaceId],
    enabled: !!apiWorkspaceId,
    refetchInterval: 6000,
    queryFn: async () => {
      if (!apiWorkspaceId) return [];
      const response = await getWorkspaceMeetingDocs({
        workspace_id: apiWorkspaceId,
        object_type: "meetings",
        doc_type: "meeting_prep",
        latest_per_object: true,
        limit: 200,
      });
      return response.docs || [];
    },
  });

  const meetingFollowupDocsQuery = useQuery<MeetingDoc[]>({
    queryKey: ["meetings-page-followup-docs", apiWorkspaceId],
    enabled: !!apiWorkspaceId,
    refetchInterval: 6000,
    queryFn: async () => {
      if (!apiWorkspaceId) return [];
      const response = await getWorkspaceMeetingDocs({
        workspace_id: apiWorkspaceId,
        object_type: "meetings",
        doc_type: "meeting_followup",
        latest_per_object: true,
        limit: 200,
      });
      return response.docs || [];
    },
  });

  const meetingCustomDocsQuery = useQuery<MeetingDoc[]>({
    queryKey: ["meetings-page-custom-docs", apiWorkspaceId],
    enabled: !!apiWorkspaceId,
    refetchInterval: 6000,
    queryFn: async () => {
      if (!apiWorkspaceId) return [];
      const response = await getWorkspaceMeetingDocs({
        workspace_id: apiWorkspaceId,
        object_type: "meetings",
        limit: 200,
      });
      return (response.docs || []).filter(
        (doc) => doc.doc_type !== "meeting_prep" && doc.doc_type !== "meeting_followup",
      );
    },
  });

  const customDocsByMeetingId = useMemo(() => {
    const map = new Map<string, MeetingDoc[]>();
    (meetingCustomDocsQuery.data || []).forEach((doc) => {
      if (!doc.object_external_id) return;
      const existing = map.get(doc.object_external_id) || [];
      existing.push(doc);
      map.set(doc.object_external_id, existing);
    });
    return map;
  }, [meetingCustomDocsQuery.data]);

  const latestPrepDocByMeetingId = useMemo(() => {
    const runMap = new Map<string, MeetingDoc>();
    (meetingPrepDocsQuery.data || []).forEach((doc) => {
      if (!doc.object_external_id || runMap.has(doc.object_external_id)) return;
      runMap.set(doc.object_external_id, doc);
    });
    return runMap;
  }, [meetingPrepDocsQuery.data]);

  const latestFollowupDocByMeetingId = useMemo(() => {
    const runMap = new Map<string, MeetingDoc>();
    (meetingFollowupDocsQuery.data || []).forEach((doc) => {
      if (!doc.object_external_id || runMap.has(doc.object_external_id)) return;
      runMap.set(doc.object_external_id, doc);
    });
    return runMap;
  }, [meetingFollowupDocsQuery.data]);

  useEffect(() => {
    if (preparingMeetingIds.size === 0) return;
    setPreparingMeetingIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      prev.forEach((meetingId) => {
        if (latestPrepDocByMeetingId.has(meetingId)) {
          next.delete(meetingId);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [latestPrepDocByMeetingId, preparingMeetingIds.size]);

  useEffect(() => {
    if (followingUpMeetingIds.size === 0) return;
    setFollowingUpMeetingIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      prev.forEach((meetingId) => {
        if (latestFollowupDocByMeetingId.has(meetingId)) {
          next.delete(meetingId);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [latestFollowupDocByMeetingId, followingUpMeetingIds.size]);

  const meetingsWithPrepState = useMemo(() => {
    const meetings = meetingsQuery.data || [];
    return meetings.map<MeetingRowData>((meeting) => {
      const prepDoc = latestPrepDocByMeetingId.get(meeting.id);
      const followupDoc = latestFollowupDocByMeetingId.get(meeting.id);
      const prepRunStatus = prepDoc ? normalizeMeetingDocStatus(prepDoc.status) : null;
      const followupRunStatus = followupDoc ? normalizeMeetingDocStatus(followupDoc.status) : null;
      const localPreparing = preparingMeetingIds.has(meeting.id);
      const localFollowingUp = followingUpMeetingIds.has(meeting.id);
      const effectiveStatus: MeetingPrepStatus | undefined = (
        prepRunStatus || (localPreparing ? "pending" : undefined)
      );
      const effectiveFollowupStatus: MeetingPrepStatus | undefined = (
        followupRunStatus || (localFollowingUp ? "pending" : undefined)
      );

      const isUpcomingMeeting = meeting.when ? meeting.when.getTime() > Date.now() : true;
      const preferredDoc = isUpcomingMeeting ? prepDoc : followupDoc;
      const fallbackDoc = isUpcomingMeeting ? followupDoc : prepDoc;
      const selectedMeetingDoc = preferredDoc || fallbackDoc;

      if (!effectiveStatus && !effectiveFollowupStatus && !selectedMeetingDoc) return meeting;

      const prepBrief = getMeetingDocMarkdown(prepDoc);
      const docBrief = getMeetingDocMarkdown(selectedMeetingDoc);

      // Clear action when the relevant doc already generated successfully
      let resolvedAction = meeting.action;
      if (resolvedAction?.type === "prep" && prepRunStatus === "success") {
        resolvedAction = null;
      } else if (resolvedAction?.type === "follow_up" && followupRunStatus === "success") {
        resolvedAction = null;
      }

      return {
        ...meeting,
        action: resolvedAction,
        prepStatus: effectiveStatus,
        followupStatus: effectiveFollowupStatus,
        prepRunId: prepDoc?.id,
        prepBrief,
        doc: selectedMeetingDoc || undefined,
        prepDoc: prepDoc || undefined,
        followupDoc: followupDoc || undefined,
        searchableText: docBrief ? `${meeting.searchableText} ${docBrief.toLowerCase()}` : meeting.searchableText,
      };
    });
  }, [
    latestPrepDocByMeetingId,
    latestFollowupDocByMeetingId,
    meetingsQuery.data,
    preparingMeetingIds,
    followingUpMeetingIds,
  ]);

  const handleMeetingAction = useCallback(async (meeting: MeetingRowData) => {
    const actionType = meeting.action?.type;
    if (actionType !== "prep" && actionType !== "follow_up") return;
    if (!apiWorkspaceId) return;

    const isFollowUp = actionType === "follow_up";
    const relevantStatus = isFollowUp ? meeting.followupStatus : meeting.prepStatus;
    if (relevantStatus === "pending" || relevantStatus === "running") return;

    const alreadyInProgress = isFollowUp
      ? followingUpMeetingIds.has(meeting.id)
      : preparingMeetingIds.has(meeting.id);
    if (alreadyInProgress) return;

    const setLoadingIds = isFollowUp ? setFollowingUpMeetingIds : setPreparingMeetingIds;
    setLoadingIds((prev) => {
      const next = new Set(prev);
      next.add(meeting.id);
      return next;
    });

    const triggerFn = isFollowUp ? triggerMeetingFollowup : triggerMeetingPrep;
    const actionLabel = isFollowUp ? "follow-up" : "prep";

    let keepLoadingUntilSynced = false;
    try {
      const response = await triggerFn({
        workspace_id: apiWorkspaceId,
        meeting_external_id: meeting.id,
        meeting_title: meeting.title,
        scheduled_at: meeting.when ? meeting.when.toISOString() : null,
        company_name: meeting.company?.name || null,
        company_domain: meeting.company?.domain || null,
        attendees: meeting.attendees.map((attendee) => attendee.name),
      });

      const run = response.data;
      if (run.status === "failed") {
        notify({
          heading: `Meeting ${actionLabel} failed to start`,
          type: "failure",
          messageList: [run.error_message || "Please try again."],
        });
      } else if (run.status === "success") {
        notify({
          heading: `Meeting ${actionLabel} ready`,
          type: "success",
          messageList: [`ORA generated the ${actionLabel} brief successfully.`],
        });
      } else {
        keepLoadingUntilSynced = true;
        notify({
          heading: `Meeting ${actionLabel} started`,
          type: "info",
          messageList: [`ORA is generating the ${actionLabel} in the background.`],
        });
      }

      queryClient.invalidateQueries({
        queryKey: ["meetings-page-prep-docs", apiWorkspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["meetings-page-followup-docs", apiWorkspaceId],
      });
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? (error.response?.data?.detail || error.message)
        : `Unable to start meeting ${actionLabel} right now.`;
      notify({
        heading: `Unable to start meeting ${actionLabel}`,
        type: "failure",
        messageList: [String(message)],
      });
    } finally {
      setLoadingIds((prev) => {
        if (keepLoadingUntilSynced) return prev;
        if (!prev.has(meeting.id)) return prev;
        const next = new Set(prev);
        next.delete(meeting.id);
        return next;
      });
    }
  }, [apiWorkspaceId, preparingMeetingIds, followingUpMeetingIds, queryClient]);

  const handleOpenMeetingDoc = useCallback((meeting: MeetingRowData & { docOverride?: MeetingDoc }) => {
    const doc = meeting.docOverride || meeting.doc;
    if (!doc) return;
    const tab: "prep" | "followup" | "custom" =
      doc.doc_type === "meeting_followup" ? "followup"
      : doc.doc_type === "meeting_prep" ? "prep"
      : "custom";
    setInitialDocTab(tab);
    setSelectedMeeting(meeting);
  }, []);

  const handleMeetingClick = useCallback((meeting: MeetingRowData) => {
    setInitialDocTab(undefined);
    setSelectedMeeting(meeting);
  }, []);

  const userTokens = useMemo(() => {
    const fullName = [authInfo.user?.firstName, authInfo.user?.lastName]
      .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
      .join(" ")
      .toLowerCase();

    const email = (authInfo.user?.email || "").toLowerCase();

    return [fullName, email].filter((token) => token.length > 0);
  }, [authInfo.user?.email, authInfo.user?.firstName, authInfo.user?.lastName]);

  const filteredMeetings = useMemo(() => {
    const meetings = meetingsWithPrepState;
    const now = new Date();
    const normalizedSearch = searchValue.trim().toLowerCase();

    const nextMeetings = meetings.filter((meeting) => {
      if (normalizedSearch && !meeting.searchableText.includes(normalizedSearch)) {
        return false;
      }

      if (activeView === "my" && userTokens.length > 0) {
        const attendeeNames = meeting.attendees.map((attendee) => attendee.name.toLowerCase());
        const isMyMeeting = userTokens.some((token) => attendeeNames.some((name) => name.includes(token)));
        if (!isMyMeeting) return false;
      }

      if (activeView === "shared" && meeting.attendees.length < 2) {
        return false;
      }

      if (dateFilter !== "all") {
        const meetingTime = meeting.when?.getTime();
        if (!meetingTime) return false;

        if (dateFilter === "upcoming" && meetingTime <= now.getTime()) return false;
        if (dateFilter === "past" && meetingTime > now.getTime()) return false;
        if (dateFilter === "today" && !meeting.when) return false;
        if (dateFilter === "today" && meeting.when && !isToday(meeting.when)) return false;
      }

      if (actionFilter !== "all") {
        const actionType = meeting.action?.type;
        if (actionFilter === "needs_action" && actionType !== "prep" && actionType !== "follow_up") {
          return false;
        }
        if (actionFilter === "followed_up" && actionType !== "followed_up") {
          return false;
        }
        if (actionFilter === "none" && actionType) {
          return false;
        }
      }

      return true;
    });

    return sortByGroupAndTime(nextMeetings);
  }, [
    meetingsWithPrepState,
    searchValue,
    activeView,
    dateFilter,
    actionFilter,
    userTokens,
  ]);

  return (
    <div className="h-full flex flex-col bg-background">
      <MeetingsHeader
        activeView={activeView}
        onViewChange={setActiveView}
        displayMode={displayMode}
        onDisplayModeChange={setDisplayMode}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters((prev) => !prev)}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
        actionFilter={actionFilter}
        onActionFilterChange={setActionFilter}
      />

      <div className="flex-1 overflow-auto bg-background">
        {meetingsQuery.isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : meetingsQuery.isError ? (
          <div className="p-6">
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              <p className="font-medium">Unable to load meetings</p>
              <p className="mt-1 text-destructive/80">We couldn&apos;t fetch meetings right now. Please try again.</p>
            </div>
          </div>
        ) : filteredMeetings.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <Calendar size={18} className="text-muted-foreground/60" />
            <p className="text-sm font-semibold text-foreground">No meetings found</p>
            <p className="text-xs text-muted-foreground">Try changing your filters or search terms.</p>
          </div>
        ) : displayMode === "list" ? (
          <MeetingsListTable
            meetings={filteredMeetings}
            onMeetingAction={handleMeetingAction}
            onDocOpen={handleOpenMeetingDoc}
            onMeetingClick={handleMeetingClick}
            preparingMeetingIds={preparingMeetingIds}
            followingUpMeetingIds={followingUpMeetingIds}
          />
        ) : (
          <MeetingsBoard
            meetings={filteredMeetings}
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            onMeetingAction={handleMeetingAction}
            onDocOpen={handleOpenMeetingDoc}
            onMeetingClick={handleMeetingClick}
            preparingMeetingIds={preparingMeetingIds}
            followingUpMeetingIds={followingUpMeetingIds}
          />
        )}
      </div>
      {selectedMeeting && (() => {
        const isUpcoming = selectedMeeting.when ? selectedMeeting.when.getTime() > Date.now() : false;
        const hasDocs = Boolean(selectedMeeting.prepDoc || selectedMeeting.followupDoc || customDocsByMeetingId.get(selectedMeeting.id)?.length);
        return (
          <ObjectDetailSheet
            open={Boolean(selectedMeeting)}
            onClose={() => { setSelectedMeeting(null); setInitialDocTab(undefined); }}
            title={selectedMeeting.title}
            badge={isUpcoming ? { label: "Upcoming" } : { label: "Past" }}
            fields={[
              { label: "Time", value: selectedMeeting.timeLabel },
              { label: "Group", value: selectedMeeting.group },
              ...(selectedMeeting.company ? [{ label: "Company", value: selectedMeeting.company.name }] : []),
              { label: "Start", value: selectedMeeting.when?.toISOString(), type: "date" as const },
              { label: "Participants", value: selectedMeeting.attendees.map((a) => a.name), type: "participants" as const },
            ]}
          >
            {hasDocs && (
              <MeetingDocTabs
                prepDoc={selectedMeeting.prepDoc}
                followupDoc={selectedMeeting.followupDoc}
                customDocs={customDocsByMeetingId.get(selectedMeeting.id) || []}
                initialTab={initialDocTab}
                resetKey={selectedMeeting.id}
              />
            )}
          </ObjectDetailSheet>
        );
      })()}
    </div>
  );
};

export default MeetingsPage;
